import { Button } from 'components/Button';
import { Card } from 'components/Card';
import { OptionField } from 'components/Field/OptionField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { PageHeader } from 'components/PageHeader';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import { doPropertyAnalysisLoad } from 'store/propertyAnalysis/actions';
import {
  selectPropertyAnalysisFilter,
  selectPropertyAnalysisFilterOptions,
} from 'store/propertyAnalysis/selectors';
import { defaultCssPropertyAnalysisFilter } from 'types/PropertyAnalysis';

const booleanOptions = [
  { name: 'Yes', internalValue: 'yes' },
  { name: 'No', internalValue: 'no' },
];

type CssArrayFilterKey =
  | 'priorities'
  | 'datasetDecisions'
  | 'heightCategories'
  | 'buildingAgeBands'
  | 'populationDensityBands';

const toBoolean = (values: string[]): boolean | undefined => {
  if (!values.length) {
    return undefined;
  }
  return values[0] === 'yes';
};

export const CssPropertyAnalysisFilterModal: React.FC<
  ModalScreenProps<ModalScreens.PropertyAnalysisFilter>
> = () => {
  const dispatch = useDispatch();
  const currentFilter = useSelector(selectPropertyAnalysisFilter);
  const filterOptions = useSelector(selectPropertyAnalysisFilterOptions);
  const [filter, setFilter] = React.useState(currentFilter);

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);

  const handleApply = React.useCallback(() => {
    dispatch(doPropertyAnalysisLoad({ filter, page: 1 }));
    handleClose();
  }, [dispatch, filter, handleClose]);

  const handleClear = React.useCallback(() => {
    setFilter(defaultCssPropertyAnalysisFilter);
  }, []);

  const setArrayFilter = React.useCallback(
    (key: CssArrayFilterKey, values: string[]) => {
      setFilter((current) => ({ ...current, [key]: values }));
    },
    [],
  );

  const setBooleanFilter = React.useCallback(
    (key: 'hasConflicts', values: string[]) => {
      setFilter((current) => ({ ...current, [key]: toBoolean(values) }));
    },
    [],
  );

  const isDefaultSelection = isEqual(filter, defaultCssPropertyAnalysisFilter);

  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Filter"
          subtitle="Property Analysis"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleApply}
        />
      }
    >
      <Card>
        <OptionField
          label="Screening priority"
          modalLabel="Screening priority"
          value={filter.priorities}
          onChange={(values) => setArrayFilter('priorities', values)}
          options={filterOptions.priorities}
          multiSelect
          modalEnableSelectAll
          modalShowQuickChoose
          showBadge={filter.priorities.length > 0}
          clearable
          labelAlignTop
          showLabelInCloseHeader
          expandedInlineFirst
          expandedInlineLast
          showFieldTypeIcon={false}
        />
        <OptionField
          label="Dataset decision"
          modalLabel="Dataset decision"
          value={filter.datasetDecisions}
          onChange={(values) => setArrayFilter('datasetDecisions', values)}
          options={filterOptions.datasetDecisions}
          multiSelect
          modalEnableSelectAll
          modalShowQuickChoose
          showBadge={filter.datasetDecisions.length > 0}
          clearable
          labelAlignTop
          showLabelInCloseHeader
          expandedInlineLast
          showFieldTypeIcon={false}
        />
        <OptionField
          label="Conflicting evidence"
          modalLabel="Conflicting evidence"
          modalText="This includes the analysis conflict flag and any stored conflict details. Prioritise Pending properties, where the dataset decision is unresolved."
          value={
            filter.hasConflicts === undefined
              ? []
              : [filter.hasConflicts ? 'yes' : 'no']
          }
          onChange={(values) => setBooleanFilter('hasConflicts', values)}
          options={booleanOptions}
          showNoValueOption
          noValueOption={{ name: 'All' }}
          showBadge={filter.hasConflicts !== undefined}
          clearable
          showLabelInCloseHeader
          showFieldTypeIcon={false}
        />
        <OptionField
          label="Height category"
          modalLabel="Height category"
          value={filter.heightCategories}
          onChange={(values) => setArrayFilter('heightCategories', values)}
          options={filterOptions.heightCategories}
          multiSelect
          modalEnableSelectAll
          modalShowQuickChoose
          showBadge={filter.heightCategories.length > 0}
          clearable
          labelAlignTop
          showLabelInCloseHeader
          expandedInlineLast
          showFieldTypeIcon={false}
        />
        <OptionField
          label="Building age band"
          modalLabel="Building age band"
          value={filter.buildingAgeBands}
          onChange={(values) => setArrayFilter('buildingAgeBands', values)}
          options={filterOptions.buildingAgeBands}
          multiSelect
          modalEnableSelectAll
          modalShowQuickChoose
          showBadge={filter.buildingAgeBands.length > 0}
          clearable
          labelAlignTop
          showLabelInCloseHeader
          expandedInlineLast
          showFieldTypeIcon={false}
        />
        <OptionField
          label="Population density band"
          modalLabel="Population density band"
          value={filter.populationDensityBands}
          onChange={(values) =>
            setArrayFilter('populationDensityBands', values)
          }
          options={filterOptions.populationDensityBands}
          multiSelect
          modalEnableSelectAll
          modalShowQuickChoose
          showBadge={filter.populationDensityBands.length > 0}
          clearable
          labelAlignTop
          showLabelInCloseHeader
          expandedInlineLast
          showFieldTypeIcon={false}
        />
      </Card>

      <Button
        text="Clear Filters"
        style={{ alignSelf: 'center' }}
        onPress={handleClear}
        disabled={isDefaultSelection}
      />
    </Page>
  );
};
